// Replay data (§23.6, §9.7). A ring of the last 6 s of decoded snapshots (events included) feeds
// the Instant Replay (each round's highlight slice, kept until the match ends) and BONK CAM. Play of
// the Match may be any KO of the match, so the raw frames around every KO of a scoring round are
// retained as well and decoded only when that replay is requested. DOM-free.
import { SNAPSHOT_EVERY, TUNE } from '@shared/constants';
import { EV } from '@shared/events';
import { ModeId } from '@shared/ids';
import type { MatchEndDTO, RoundEndDTO, RoundInitDTO } from '@shared/protocol/messages';
import { decodeSnapshot, type Snapshot } from '@shared/protocol/snapshot';
import type { RoundConfig } from '@shared/types';

export const REPLAY_BUFFER = {
  /** 6 s of 30 Hz snapshots. */
  ringSize: 180,
  /** BONK CAM covers the last 2.5 s before the local KO arrived. */
  bonkCamTicks: 150,
  /** Snapshots kept beyond each end of a window so interpolation works at its edges. */
  marginTicks: SNAPSHOT_EVERY,
} as const;

/** A replayable span of one round. Ticks are round ticks; `snapshots` ascend by tick. */
export interface ReplaySlice {
  roundId: number;
  cfg: RoundConfig;
  /** Room tick at which round tick 0 occurred. */
  startRoomTick: number;
  startTick: number;
  endTick: number;
  focusSlot: number;
  killerSlot: number;
  snapshots: readonly Snapshot[];
}

interface RingEntry { snap: Snapshot; raw: Uint8Array }

/** Raw snapshot frames of one scoring round, kept around each KO window (keyed by round tick). */
interface KoArchive { init: RoundInitDTO; windows: { start: number; end: number }[]; frames: Map<number, Uint8Array> }

/** True when `snaps` (ascending) span [start, end]. */
function covers(snaps: readonly Snapshot[], start: number, end: number): boolean {
  return snaps.length > 0 && snaps[0].roundTick <= start && snaps[snaps.length - 1].roundTick >= end;
}

function isScoring(cfg: RoundConfig): boolean {
  return cfg.modeId !== ModeId.LOBBY && cfg.modeId !== ModeId.PODIUM;
}

export class ReplayBuffer {
  /** The newest BONK CAM slice (set on the local bean's KO). */
  bonkCam: ReplaySlice | null = null;
  private readonly ring: RingEntry[] = [];
  private next = 0;
  private round: RoundInitDTO | null = null;
  private readonly highlights = new Map<number, ReplaySlice>();
  private readonly archives = new Map<number, KoArchive>();

  /** Starts a round (also on a reconnect's repeated `round`): the ring restarts empty. */
  setRound(init: RoundInitDTO): void {
    this.round = init;
    this.ring.length = 0;
    this.next = 0;
    this.bonkCam = null;
    if (isScoring(init.cfg) && !this.archives.has(init.roundId)) this.archives.set(init.roundId, { init, windows: [], frames: new Map() });
  }

  /** Adds a decoded snapshot of the current round together with its raw frame. */
  push(snap: Snapshot, raw: Uint8Array): void {
    if (!this.round) return;
    const entry = { snap, raw };
    if (this.ring.length < REPLAY_BUFFER.ringSize) this.ring.push(entry);
    else this.ring[this.next] = entry;
    this.next = (this.next + 1) % REPLAY_BUFFER.ringSize;

    const archive = this.archives.get(this.round.roundId);
    if (!archive) return;
    for (const ev of snap.events) if (ev.type === EV.KO) this.addKoWindow(archive, ev.tick);
    if (archive.windows.some((w) => snap.roundTick >= w.start && snap.roundTick <= w.end)) archive.frames.set(snap.roundTick, raw);
  }

  /** Ring snapshots with round ticks in [startTick, endTick] plus one margin snapshot per side. */
  slice(startTick: number, endTick: number): Snapshot[] {
    const lo = startTick - REPLAY_BUFFER.marginTicks;
    const hi = endTick + REPLAY_BUFFER.marginTicks;
    const out: Snapshot[] = [];
    const n = this.ring.length;
    const first = n < REPLAY_BUFFER.ringSize ? 0 : this.next;
    for (let i = 0; i < n; i++) {
      const s = this.ring[(first + i) % n].snap;
      if (s.roundTick >= lo && s.roundTick <= hi) out.push(s);
    }
    return out;
  }

  /**
   * Copies the round's highlight window (Instant Replay) and keeps it until the match ends. A final
   * KO older than the 6 s ring (a round that ran on to the cap) comes from the KO archive instead.
   */
  captureHighlight(end: RoundEndDTO): ReplaySlice | null {
    const h = end.highlight;
    if (!h || !this.round || end.roundId !== this.round.roundId) return null;
    let snaps = this.slice(h.startTick, h.endTick);
    if (!covers(snaps, h.startTick, h.endTick)) {
      const archived = this.archived(end.roundId, h.startTick, h.endTick);
      if (archived.length > snaps.length) snaps = archived;
    }
    const slice = this.makeSlice(this.round, h.startTick, h.endTick, h.focusSlot, h.killerSlot, snaps);
    if (!slice) return null;
    this.highlights.set(end.roundId, slice);
    return slice;
  }

  /** Copies the last 2.5 s (local KO) as the BONK CAM slice. */
  captureBonkCam(focusSlot: number, killerSlot: number): ReplaySlice | null {
    if (!this.round || this.ring.length === 0) return null;
    const newest = this.ring[(this.next - 1 + this.ring.length) % this.ring.length].snap;
    const start = newest.roundTick - REPLAY_BUFFER.bonkCamTicks;
    this.bonkCam = this.makeSlice(this.round, start, newest.roundTick, focusSlot, killerSlot, this.slice(start, newest.roundTick));
    return this.bonkCam;
  }

  highlight(roundId: number): ReplaySlice | null {
    return this.highlights.get(roundId) ?? null;
  }

  /** The Play of the Match window, from its round's highlight or from the archived KO frames. */
  playOfMatch(potm: MatchEndDTO['playOfMatch']): ReplaySlice | null {
    if (!potm) return null;
    const lo = potm.startTick - REPLAY_BUFFER.marginTicks;
    const hi = potm.endTick + REPLAY_BUFFER.marginTicks;
    const hl = this.highlights.get(potm.roundId);
    if (hl && hl.startTick <= potm.startTick && hl.endTick >= potm.endTick) {
      const snaps = hl.snapshots.filter((s) => s.roundTick >= lo && s.roundTick <= hi);
      return this.makeSlice(hl, potm.startTick, potm.endTick, potm.focusSlot, potm.killerSlot, snaps);
    }
    const archive = this.archives.get(potm.roundId);
    if (!archive) return null;
    return this.makeSlice(archive.init, potm.startTick, potm.endTick, potm.focusSlot, potm.killerSlot, this.archived(potm.roundId, potm.startTick, potm.endTick));
  }

  /** Decoded archived frames of round `roundId` in [start, end] plus the margins, by tick. */
  private archived(roundId: number, start: number, end: number): Snapshot[] {
    const archive = this.archives.get(roundId);
    if (!archive) return [];
    const lo = start - REPLAY_BUFFER.marginTicks;
    const hi = end + REPLAY_BUFFER.marginTicks;
    return [...archive.frames]
      .filter(([t]) => t >= lo && t <= hi)
      .sort((x, y) => x[0] - y[0])
      .map(([, raw]) => decodeSnapshot(raw, archive.init.cfg.mapId));
  }

  /** Forgets everything (left the room). */
  reset(): void {
    this.round = null;
    this.ring.length = 0;
    this.next = 0;
    this.clearMatch();
  }

  /** Forgets the match's highlights and archives (back to the lobby). */
  clearMatch(): void {
    this.highlights.clear();
    this.archives.clear();
    this.bonkCam = null;
    if (this.round && isScoring(this.round.cfg)) this.archives.set(this.round.roundId, { init: this.round, windows: [], frames: new Map() });
  }

  private addKoWindow(archive: KoArchive, koTick: number): void {
    const win = {
      start: koTick - TUNE.highlight.preTicks - REPLAY_BUFFER.marginTicks,
      end: koTick + TUNE.highlight.postTicks + REPLAY_BUFFER.marginTicks,
    };
    archive.windows.push(win);
    for (const { snap, raw } of this.ring) if (snap.roundTick >= win.start && snap.roundTick <= win.end) archive.frames.set(snap.roundTick, raw);
  }

  private makeSlice(
    round: { roundId: number; cfg: RoundConfig; startRoomTick: number },
    startTick: number, endTick: number, focusSlot: number, killerSlot: number, snapshots: Snapshot[],
  ): ReplaySlice | null {
    if (snapshots.length === 0) return null;
    return { roundId: round.roundId, cfg: round.cfg, startRoomTick: round.startRoomTick, startTick, endTick, focusSlot, killerSlot, snapshots };
  }
}
